
import DisplayItem from '../models/DisplayItem';
import Item from '../models/Item';
import EventBusConstants from '../constants/EventBusConstants';
import { inject } from 'vue';
import { Prop, Vue, Watch } from 'vue-property-decorator';
import PlayerEmitter from '@/event/PlayerEmitter';
import TestController from '@/controllers/TestController';
import DeliveryOptionConstants from '@/controllers/options/DeliveryOptionConstants';
import { PauseState } from '@/controllers/pause/PauseManager';
import TextHelp from '../util/TextHelp';

export default class ItemContainer extends Vue {
  @Prop() readonly testController: TestController | undefined;
  @Prop() readonly item: DisplayItem | undefined;
  @Prop({ default: true }) readonly showQuestionNumber: boolean | undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private itemElement: any | undefined = undefined;
  private emitter: PlayerEmitter | undefined = inject('playerEmitter');
  private adjustmentHeight = 0;
  private get displayData() {
    return this.item!.qti;
  }

  private getAdjustedFlagPosition(): string {
    const additionalHeight = 5 + (this.showTheQuestionNumber() ? 40 : 0);
    return `top: ${this.adjustmentHeight + additionalHeight}px; `;
  }

  private getAdjustedQuestionNumberPosition(): string {
    return `top: ${this.adjustmentHeight + 5}px; `;
  }

  private getAdjustedContainerStyle(): string {
    let style = `max-height: calc(100% - ${this.adjustmentHeight}px); 
    max-height: -webkit-calc(100% - ${this.adjustmentHeight}px); 
    max-height: -moz-calc(100% - ${this.adjustmentHeight}px); `;
    if (this.adjustmentHeight && this.adjustmentHeight === 0) {
      style += 'border-top-left-radius: 5px; border-top-right-radius: 5px; ';
    }

    if (this.showTheQuestionNumber() || this.showFlagIcon()) {
      style += 'padding-left: 45px; ';
    }
    return style;
  }

  @Watch('item')
  private onPropertyChange(newItem: Item, oldItem: Item): void {
    this.updateStateFromItemElement(newItem, oldItem);
    this.testController!.persistenceManager?.save(null, true);
  }

  updated() {
    this.updateItemElement();
  }

  mounted() {
    this.updateItemElement();
    this.emitter?.on(EventBusConstants.HARD_TIMER_EVENT, (data) => this.handleHardTimerEvent(data));
    this.emitter?.on(EventBusConstants.PNP_ITEM_CLICKED, (data) => this.handlePnpItem(data));
    this.testController?.setCurrentItem(this.item!, undefined);
    this.$emit('ready');

    this.adjustBottomLayout(false);

    this.emitter?.on(EventBusConstants.SCHEDULED_EVENT, (data) => {
      if (data.get('type') === 'saveRequest') {
        if (this.testController && this.testController.persistenceManager && this.item) {
          this.updateStateFromItemElement(undefined, this.item);
          this.testController!.persistenceManager?.save(this.item, false);
        }
      }
    });

    this.emitter?.on(EventBusConstants.PAUSE_EVENT, (data) => this.handlePauseEvent(data));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handlePauseEvent(data: Map<string, any>): void {
    const currentItemElement = this.itemElement;
    if (currentItemElement) {
      if (data.get('type') === PauseState.PAUSE) {
        currentItemElement.stop();
      } else if (data.get('type') !== PauseState.PAUSE) {
        currentItemElement.stop();
        currentItemElement.start();
      }
    }
  }

  beforeUnmount() {
    this.updateStateFromItemElement(undefined, this.item);
  }

  saveItem(skipUpdate: boolean | undefined, callback: () => void): void {
    if (!skipUpdate && this.item) {
      this.updateStateFromItemElement(undefined, this.item);
      this.testController!.persistenceManager?.save(this.item, true, true);
    }
    callback();
  }

  addTopBorderRadius(value: boolean): void {
    this.adjustTopBorderLayout(value);
  }

  setAdjustmentHeight(value: number): void {
    this.adjustmentHeight = value;
  }

  private showTheQuestionNumber(): boolean {
    return (
      this.showQuestionNumber !== undefined &&
      this.showQuestionNumber === true &&
      this.item !== undefined &&
      this.item instanceof Item
    );
  }

  private getQuestionNumber(): string {
    if (this.item && this.item instanceof Item) {
      return this.item.section.items.indexOf(this.item as Item) + 1 + '';
    } else {
      return '';
    }
  }

  adjustBottomLayout(removeBottomRadius: boolean): void {
    const innerContainer = this.$el.querySelector('#inner-container');
    if (removeBottomRadius) {
      innerContainer.classList.remove('radius-bottom-border');
    } else {
      innerContainer.classList.add('radius-bottom-border');
    }
  }

  private adjustTopBorderLayout(addRadius: boolean): void {
    const innerContainer = this.$el.querySelector('#inner-container');
    if (addRadius) {
      innerContainer.classList.add('radius-top-border');
    } else {
      innerContainer.classList.remove('radius-top-border');
    }
  }

  private notifySizeChange(): void {
    this.$emit('heightChange', this.$el.querySelector('#inner-container').getBoundingClientRect().height);
  }

  private handleHardTimerEvent(data) {
    const currentItemElement = this.itemElement;
    if (currentItemElement) {
      if (data.get('testExpired')) {
        currentItemElement.stop();
      } else {
        if (data.get('testPaused')) {
          currentItemElement.stop();
        } else if (!data.get('testPaused')) {
          currentItemElement.stop();
          currentItemElement.start();
        }
      }
    }
  }

  private showFlagIcon() {
    return this.item instanceof Item && this.item.isFlagged;
  }

  private handlePnpItem(data) {
    console.log(`handlePnpItem()`, data);

    let tool = data.get('name').split('.')[0];
    let option = data.get('name').split('.')[1];
    let iw = this.$el.querySelector('#item-wrapper');

    // Color Scheme
    if (tool == 'color-scheme') {
      // Remove all classes that start with color-contrast
      iw.classList = iw.className
        .split(' ')
        .filter((c) => !c.startsWith('color-contrast'))
        .join(' ')
        .trim();

      // Add current color
      iw.classList.add(`color-contrast-${option}`);
      return;
    }

    // Font Size
    if (tool == 'font-size') {
      iw.setAttribute('style', `font-size: ${data.get('name').split('.')[1]}`);
    }

    // Zoom
    if (tool == 'zoom') {
      iw.style.zoom = option;
    }

    // Line Reader
    if (tool == 'line-reader') {
      let curState = document.getElementById('texthelpTopMask') != undefined ? 'on' : 'off';
      if (option != curState) {
        TextHelp.getAPI().then((api) => {
          api.studyTools.toggleScreenMask();
        });
      }
    }

    // Choice Masking
    if (tool == 'choice-masking') {
      if (option == 'on') {
        this.itemElement?.interactions.forEach((interaction) => {
          interaction.readOnly = true;
        });
        this.itemElement?.addEventListener('click', this.choiceMaskClick);
      } else if (option == 'off') {
        this.itemElement?.interactions.forEach((interaction) => {
          interaction.readOnly = false;
        });
        this.itemElement?.removeEventListener('click', this.choiceMaskClick);
      } else {
        this.itemElement?.querySelectorAll('qti-simple-choice').forEach((choice) => {
          choice.removeAttribute('strike');
        });
      }
    }

    if (tool == DeliveryOptionConstants.FLAG_ITEM) {
      this.testController?.setItemFlag(this.item);
    }
  }

  private choiceMaskClick(ev) {
    let choice = ev.target.tagName == 'QTI-SIMPLE-CHOICE' ? ev.target : ev.target.closest('QTI-SIMPLE-CHOICE');
    if (choice) {
      choice.removeAttribute('selected');
      choice.toggleAttribute('strike');
    }
  }

  private updateItemElement() {
    const previousItemElement = this.itemElement;
    if (previousItemElement) {
      previousItemElement.stop();
      previousItemElement.removeEventListener('changed', this.changeEventHandler);
    }

    this.itemElement = undefined;
    const itemElements = document.getElementsByTagName('qti-assessment-item');
    if (itemElements.length > 1) {
      this.emitter?.emit(EventBusConstants.APPLICATION_ERROR, [
        ['message', 'ItemContainer: Unexpected item construction'],
      ]);
      return;
    } else if (itemElements.length === 1) {
      this.itemElement = itemElements[0];
    }

    const currentItemElement = this.itemElement;
    const currentItem: DisplayItem | undefined = this.item;
    if (currentItemElement) {
      currentItemElement.addEventListener('changed', this.changeEventHandler);
      currentItemElement.stop();
      currentItemElement.start();
      if (currentItem && currentItem.state) {
        if (currentItem.identifier !== currentItemElement.identifier) {
          this.emitter?.emit(EventBusConstants.APPLICATION_ERROR, [
            ['message', 'ItemContainer.updateItemElement: Identifier mismatch'],
          ]);
          return;
        }
        try {
          currentItemElement.setState(JSON.parse(currentItem.state));
        } catch (error: unknown) {
          this.emitter?.emit(EventBusConstants.APPLICATION_ERROR, [
            ['message', 'Problem setting item element state: ' + error],
          ]);
          return;
        }
      }
    }

    if (
      this.testController!.navigationController!.isBeginningOfTest() &&
      currentItem &&
      currentItem.state.trim().length === 0
    ) {
      this.testController!.persistenceManager?.save(null, true);
    }
  }

  private changeEventHandler(): void {
    this.updateStateFromItemElement(undefined, this.item);
  }

  private updateStateFromItemElement(newItem, oldItem) {
    const currentItemElement = this.itemElement;

    if (currentItemElement) {
      if (oldItem.identifier === currentItemElement.identifier) {
        try {
          this.testController!.stateController?.updateStateWithItemElementState(oldItem, currentItemElement.getState());
        } catch (error: unknown) {
          this.emitter?.emit(EventBusConstants.APPLICATION_ERROR, [
            ['message', 'Problem setting item state: ' + error],
          ]);
          return;
        }
      } else {
        this.emitter?.emit(EventBusConstants.APPLICATION_ERROR, [
          ['message', 'ItemContainer.updateStateFromItemElement: Identifier mismatch'],
        ]);
        return;
      }
    }
    this.testController?.setCurrentItem(newItem, oldItem);
  }
}
